import { Component, OnInit } from '@angular/core';
import { ContractorService } from '../../Services/contractor.service';
import { AuthService } from '../services/auth.service';
import { ConnectService, ConnectStatus, ContractorPayment } from '../services/connect.service';
import { Contractor } from '../../objects/Contractor';

interface PayoutRow {
  contractor: Contractor;
  status?: ConnectStatus;
  loadingStatus: boolean;
  payAmount: number | null;
  payDescription: string;
  paying: boolean;
  error: string;
  message: string;
  payments: ContractorPayment[];
}

// Provider-facing: onboard sub-contractors to Stripe Connect and pay them out.
// Each contractor completes Stripe-hosted onboarding once; after that "Pay"
// sends a transfer from the platform balance to their connected account.
@Component({
  selector: 'app-contractor-payouts',
  templateUrl: './contractor-payouts.component.html',
  styleUrls: ['./contractor-payouts.component.css']
})
export class ContractorPayoutsComponent implements OnInit {
  rows: PayoutRow[] = [];
  loading = false;
  error = '';

  constructor(
    private contractorService: ContractorService,
    private connectService: ConnectService,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    this.loading = true;
    const user = this.authService.getCurrentUser();
    const providerId = user ? user.id : null;

    const source$ = providerId
      ? this.contractorService.getContractorsByProvider(providerId)
      : this.contractorService.getContractors();

    source$.subscribe({
      next: contractors => {
        this.rows = contractors.map(c => ({
          contractor: c,
          loadingStatus: true,
          payAmount: null,
          payDescription: '',
          paying: false,
          error: '',
          message: '',
          payments: []
        }));
        this.loading = false;
        this.rows.forEach(r => {
          this.refreshStatus(r);
          this.loadPayments(r);
        });
      },
      error: () => { this.error = 'Could not load sub-contractors.'; this.loading = false; }
    });
  }

  refreshStatus(r: PayoutRow): void {
    r.loadingStatus = true;
    this.connectService.getStatus(r.contractor.id).subscribe({
      next: s => { r.status = s; r.loadingStatus = false; },
      error: () => { r.loadingStatus = false; }
    });
  }

  loadPayments(r: PayoutRow): void {
    this.connectService.getContractorPayments(r.contractor.id).subscribe({
      next: p => r.payments = p,
      error: () => {}
    });
  }

  startOnboarding(r: PayoutRow): void {
    r.error = '';
    this.connectService.createOnboardingLink(r.contractor.id).subscribe({
      next: link => (window.location.href = link.url),
      error: err => (r.error = err?.error?.message || 'Could not start payout setup.')
    });
  }

  openDashboard(r: PayoutRow): void {
    this.connectService.createDashboardLink(r.contractor.id).subscribe({
      next: link => window.open(link.url, '_blank'),
      error: err => (r.error = err?.error?.message || 'Could not open the Stripe dashboard.')
    });
  }

  pay(r: PayoutRow): void {
    r.error = '';
    r.message = '';
    if (!r.payAmount || r.payAmount <= 0) { r.error = 'Enter an amount greater than zero.'; return; }
    r.paying = true;
    this.connectService.payContractor({
      contractorId: r.contractor.id,
      amount: r.payAmount,
      description: r.payDescription
    }).subscribe({
      next: p => {
        r.paying = false;
        r.message = `Sent $${r.payAmount?.toFixed(2)} — ${p.status}.`;
        r.payAmount = null;
        r.payDescription = '';
        this.loadPayments(r);
      },
      error: err => { r.paying = false; r.error = err?.error?.message || 'Payment failed.'; }
    });
  }
}
